import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generatePath } from 'react-router-dom';
import { Response, Pagination } from '@ui/src';
import { RiskScoreItem, IncorporationDateListItem } from '../../../types';
import { corporateClientsRiskApi } from '../services';
import ROUTES from '../../../../routes';

export interface CorporateClientsIncorporationDatesListState {
  values?: IncorporationDateListItem[];
  pagination?: Pagination;
}

const initialState: CorporateClientsIncorporationDatesListState = {
  values: undefined,
};

const getFromYearLabel = (year: number, array: number[]) => {
  let fromYear = '';

  if (year === 0) {
    fromYear = 'Less then 1 year';
  } else {
    const closestRight = Math.min(...array.filter((number) => number > year));

    const maxNumber = Math.max(...array);

    if (closestRight && year !== maxNumber) {
      fromYear = `More than ${year} year${
        year > 4 ? 's' : ''
      } less than ${closestRight}`;
    } else if (year === maxNumber) {
      fromYear = `More than ${year} year${year > 4 ? 's' : ''}`;
    }
  }

  return fromYear;
};

export const corporateClientsIncorporationDatesSlice = createSlice({
  name: 'corporateClientsIncorporationDates',
  initialState,
  reducers: {
    getIncorporationDatesSuccess: (
      state,
      action: PayloadAction<IncorporationDateListItem[]>,
    ) => {
      const fromYearArray = action.payload.map((item) => item.fromYear);

      state.values = action.payload.map((item) => ({
        ...item,
        fromYearStart: getFromYearLabel(item.fromYear, fromYearArray),
      }));
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      corporateClientsRiskApi.endpoints.getIncorporationDates.matchFulfilled,
      (state, action: PayloadAction<Response<RiskScoreItem>>) => {
        const fromYearArray = action.payload.data.edges.map(
          (item) => item.node.fromYear,
        );

        state.values = action.payload.data.edges.map((item) => ({
          id: item.node.id,
          fromYearStart: getFromYearLabel(item.node.fromYear, fromYearArray),
          fromYear: item.node.fromYear,
          riskScore: item.node.riskScore,
          riskCategory: item.node.riskScoreRange.name,
          scoreAction: item.node.riskScoreRange.action,
          url: generatePath(
            ROUTES.CORPORATE_CLIENTS_RISK_DATE_OF_INCORPORATION_EDIT,
            {
              dateId: item.node.id,
            },
          ),
        }));

        state.pagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      corporateClientsRiskApi.endpoints.updateIncorporationDate.matchFulfilled,
      (state, action: PayloadAction<{ data: RiskScoreItem }>) => {
        const { data } = action.payload;

        const fromYearArray = state?.values?.map((item) => item.fromYear);

        const nextStateValues = state.values?.map((item) => {
          let result = { ...item };

          if (result.id === data.id) {
            result = {
              id: data.id,
              fromYearStart: getFromYearLabel(
                data.fromYear,
                fromYearArray as number[],
              ),
              fromYear: data.fromYear,
              riskScore: data.riskScore,
              riskCategory: data.riskScoreRange.name,
              scoreAction: data.riskScoreRange.action,
              url: generatePath(
                ROUTES.CORPORATE_CLIENTS_RISK_DATE_OF_INCORPORATION_EDIT,
                {
                  dateId: data.id,
                },
              ),
            };
          }

          return result;
        });

        state.values = nextStateValues?.map((item) => ({
          ...item,
          fromYearStart: getFromYearLabel(
            item.fromYear,
            nextStateValues?.map((value) => value.fromYear),
          ),
        }));
      },
    );
  },
});

export const { getIncorporationDatesSuccess } =
  corporateClientsIncorporationDatesSlice.actions;

export default corporateClientsIncorporationDatesSlice.reducer;
